import {
  faCheck,
  faWarning,
  faTriangleExclamation,
  type IconDefinition
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { type ToasterToast, useToast } from "~/components/hooks/use-toast";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport
} from "~/components/ui/toast";

export const Toaster = () => {
  const { toasts, dismiss: dismissToast } = useToast();

  // With TOAST_LIMIT = 1, there's only ever one toast, so we use its position for the viewport
  const viewportPosition = toasts[0]?.position;

  return (
    <ToastProvider>
      {toasts.map(
        ({
          id,
          title,
          description,
          action,
          variant,
          icon,
          position: toastPosition,
          ...props
        }) => {
          const iconDef = getIconDefinition({ icon, variant });

          return (
            <Toast
              key={id}
              variant={variant}
              position={toastPosition}
              {...props}
            >
              <div className="flex items-center gap-4">
                {iconDef ? <FontAwesomeIcon icon={iconDef} size="2x" /> : null}

                <div className="grid gap-1">
                  {title ? <ToastTitle>{title}</ToastTitle> : null}

                  {description ? (
                    <ToastDescription>{description}</ToastDescription>
                  ) : null}
                </div>
              </div>
              {action}
              <ToastClose
                onClick={() => {
                  dismissToast(id);
                }}
              />
            </Toast>
          );
        }
      )}
      <ToastViewport position={viewportPosition} />
    </ToastProvider>
  );
};

function getIconDefinition({
  icon,
  variant
}: Pick<ToasterToast, "icon" | "variant">): IconDefinition | null {
  if (icon) {
    return icon;
  }

  switch (variant) {
    case "destructive":
      return faWarning;
    case "success":
      return faCheck;
    case "warning":
      return faTriangleExclamation;
    default:
      return null;
  }
}
